import {
  EventName,
  EventType,
  RCEvent,
  NetworkEventData,
} from "../eventhub/eventmodels.js";
import { RidecellCallback } from "../models/ridecellcallback.js";
import { NetworkRequests } from "../networkrequestsbuilder.js";
import { EventController } from "../eventhub/eventcontroller.js";
import { CustomerAuthenticationController } from "./customerauthentication.js";

const callbackMethod = (resolve, reject) => {
  const registrationCallback = new RidecellCallback();
  registrationCallback.onSuccess = function success(result) {
    resolve(result);
  };
  registrationCallback.onFailure = (error) => {
    reject(error);
  };
  return registrationCallback;
};

export class RegistrationController {
  isRegistrationAllowed(lat, lng) {
    return new Promise((resolve, reject) => {
      const queryParams = {
        role: "CUSTOMER",
        lat: lat,
        lng: lng,
      };
      const registrationInfo = new NetworkEventData(
        EventType.GET,
        NetworkRequests.isRegistrationAllowed,
        queryParams
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          registrationInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  getConsentOptions(queryParams) {
    return new Promise((resolve, reject) => {
      const consentOptions = new NetworkEventData(
        EventType.GET,
        NetworkRequests.consentOptions,
        queryParams
      );

      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          consentOptions,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  getPolicyDocs(queryParams) {
    return new Promise((resolve, reject) => {
      const policyDocsInfo = new NetworkEventData(
        EventType.GET,
        NetworkRequests.policyDocs,
        queryParams
      );

      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          policyDocsInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  getRegistrationSteps({ customerId, queryParams }) {
    return new Promise((resolve, reject) => {
      const registrationStepsInfo = new NetworkEventData(
        EventType.GET,
        NetworkRequests.registrationSteps(customerId),
        queryParams
      );

      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          registrationStepsInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  createCustomer(data) {
    return new Promise((resolve, reject) => {
      const customerAuthController = new CustomerAuthenticationController();

      const customerData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.createCustomer,
        null, //queryData
        data, //formData
        false //isPatch
        // true //isMultipart
      );

      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        customerAuthController.saveCustomerToCache(result);
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, customerData, callback));
    });
  }

  updateCustomer({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerAuthController = new CustomerAuthenticationController();
      const customerData = new NetworkEventData(
        EventType.PATCH,
        NetworkRequests.updateCustomer(customerId),
        null,
        data
      );

      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        customerAuthController.saveCustomerToCache(result);
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, customerData, callback));
    });
  }

  verifyPhoneNumber({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerAuthController = new CustomerAuthenticationController();

      const queryParams = {
        role: "CUSTOMER",
      };
      const phoneNumberInfo = new NetworkEventData(
        EventType.POST,
        NetworkRequests.verifyPhoneNumber(customerId),
        queryParams, //queryData
        data, //formData
        false //isPatch
      );

      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        customerAuthController.saveCustomerToCache(result);
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, phoneNumberInfo, callback));
    });
  }

  getRegistrationInfo(lat, lng, customerId, product) {
    return new Promise((resolve, reject) => {
      const queryParams = {
        role: "CUSTOMER",
        lat,
        lng,
        product,
      };
      const registrationStepsInfo = new NetworkEventData(
        EventType.GET,
        NetworkRequests.registrationInfo(customerId),
        queryParams
      );

      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          registrationStepsInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  saveMailingAddress({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerAuthController = new CustomerAuthenticationController();

      const mailingAddressInfo = new NetworkEventData(
        EventType.POST,
        NetworkRequests.saveMailingAddress(customerId),
        null,
        data
      );
      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        customerAuthController.saveCustomerToCache(result);
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(
        new RCEvent(EventName.NETWORK, mailingAddressInfo, callback)
      );
    });
  }

  saveDriverLicense({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerAuthController = new CustomerAuthenticationController();
      const licenseInfo = new NetworkEventData(
        EventType.POST,
        NetworkRequests.saveDriverLicense(customerId),
        null,
        data
      );
      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        customerAuthController.saveCustomerToCache(result);
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, licenseInfo, callback));
    });
  }

  uploadDocuments({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const formdata = new FormData();
      formdata.append(
        "document_specification_id",
        data.document_specification_id
      );
      formdata.append("document_image", data.document_image);
      const documentInfo = new NetworkEventData(
        EventType.POST,
        NetworkRequests.uploadDocuments(customerId),
        null,
        formdata,
        false,
        true
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          documentInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  completeDocumentSubmission({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerData = new NetworkEventData(
        EventType.PATCH,
        NetworkRequests.updateDocuments(customerId),
        null,
        data
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          customerData,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  resendVerificationCode({ customerId, data }) {
    return new Promise((resolve, reject) => {
      const customerData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.resendVerificationCode(customerId),
        null,
        data
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          customerData,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  /**
   * Passes the event to event controller.
   * @param {RCEvent} event
   */
  sendEvent(event) {
    EventController.addEvent(event);
  }

  getDocumentSpecificationForMIV() {
    return new Promise((resolve, reject) => {
      const requestData = new NetworkEventData(
        EventType.GET,
        NetworkRequests.manualIdentityVerificationDocuments
      );

      const callback = new RidecellCallback();
      callback.onSuccess = (result) => {
        resolve(result);
      };
      callback.onFailure = (error) => {
        reject(error);
      };
      this.sendEvent(new RCEvent(EventName.NETWORK, requestData, callback));
    });
  }

  fetchPaymentSheet(data) {
    return new Promise((resolve, reject) => {
      const paymentInfo = new NetworkEventData(
        EventType.POST,
        NetworkRequests.paymentSheet,
        null,
        data
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          paymentInfo,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  createVeriffSession({ customerId }) {
    return new Promise((resolve, reject) => {
      const veriffSessionData = new NetworkEventData(
        EventType.POST,
        NetworkRequests.createVeriffSession(customerId)
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          veriffSessionData,
          callbackMethod(resolve, reject)
        )
      );
    });
  }

  getRegistrationStatus({ customerId }) {
    return new Promise((resolve, reject) => {
      const registrationStatusData = new NetworkEventData(
        EventType.GET,
        NetworkRequests.getRegistrationStatus(customerId)
      );
      this.sendEvent(
        new RCEvent(
          EventName.NETWORK,
          registrationStatusData,
          callbackMethod(resolve, reject)
        )
      );
    });
  }
}
